import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from "@angular/core"
import { ColorPaletteService } from "@shared/services/color-palette.service"
import { IconImageService } from "@shared/services/icon-image.service"

@Component({
  selector: "flow-tab",
  templateUrl: "./flow-tab.component.html",
  styleUrl: "./flow-tab.component.scss",
  standalone: false,
})
export class FlowTabComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() tabList: FlowTab[] = []
  private _selectedTab: string = ""
  @Input()
  set selectedTab(value: string) {
    if (value === this._selectedTab) return
    this._selectedTab = value
    this.updatePill()
  }
  get selectedTab(): string {
    return this._selectedTab
  }
  @Input() width: string = "100%"
  @Input() selectedColor: FlowColors = "brand"
  @Input() borderBottom: string = "1px solid " + this.colorService.neutral200

  @Output() tabSelected = new EventEmitter<string>()

  @ViewChild("tabsContainer") tabsContainer?: ElementRef<HTMLElement>
  @ViewChildren("tabWrapper") tabWrappers?: QueryList<ElementRef<HTMLElement>>

  public pillLeft = 0
  public pillWidth = 0
  public pillColor = "transparent"
  public pillVisible = false
  public pillAnimated = false

  private resizeObserver?: ResizeObserver

  constructor(
    public iconService: IconImageService,
    private colorService: ColorPaletteService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.tabWrappers?.changes.subscribe(() => this.scheduleUpdate())
    this.scheduleUpdate()
    setTimeout(() => (this.pillAnimated = true), 50)

    if (typeof ResizeObserver !== "undefined" && this.tabsContainer) {
      this.resizeObserver = new ResizeObserver(() => this.updatePill())
      this.resizeObserver.observe(this.tabsContainer.nativeElement)
    }

    const fonts = (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts
    if (fonts?.ready) {
      fonts.ready.then(() => this.updatePill())
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["tabList"] || changes["selectedColor"]) {
      this.scheduleUpdate()
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    this.updatePill()
  }

  private scheduleUpdate(): void {
    Promise.resolve().then(() => {
      this.cdr.detectChanges()
      this.updatePill()
      requestAnimationFrame(() => this.updatePill())
    })
  }

  private updatePill(): void {
    const wrappers = this.tabWrappers?.toArray()
    if (!wrappers || wrappers.length === 0) {
      this.pillVisible = false
      return
    }
    const index = this.tabList.findIndex((t) => t.title === this._selectedTab)
    const tab = index >= 0 ? this.tabList[index] : undefined
    const el = index >= 0 ? wrappers[index]?.nativeElement : undefined
    if (!tab || tab.isDisabled || !el) {
      this.pillVisible = false
      return
    }
    this.pillLeft = el.offsetLeft
    this.pillWidth = el.offsetWidth
    this.pillColor = this.getPillBackgroundColor(tab)
    this.pillVisible = true
  }

  public selectTab(tab: FlowTab) {
    if (tab.isDisabled) {
      return
    } else {
      this._selectedTab = tab.title
      this.tabSelected.emit(tab.title)
      this.updatePill()
    }
  }

  public getSelectedColor(tab: FlowTab): string {
    if (tab.isDisabled) {
      return ""
    } else {
      if (this.selectedTab === tab.title) {
        return this.getTextColor()
      } else {
        return ""
      }
    }
  }

  private getTextColor(): string {
    switch (this.selectedColor) {
      case "neutral":
        return this.colorService.neutral700
      case "red":
        return this.colorService.red700
      case "orange":
        return this.colorService.orange700
      case "yellow":
        return this.colorService.yellow700
      case "green":
        return this.colorService.green700
      case "teal":
        return this.colorService.teal700
      case "blue":
        return this.colorService.blue700
      case "brand":
        return this.colorService.brand700
      case "purple":
        return this.colorService.purple700
      case "pink":
        return this.colorService.pink700
    }
  }

  public getPillBackgroundColor(tab: FlowTab): string {
    if (tab.isDisabled) {
      return "transparent"
    } else {
      switch (this.selectedColor) {
        case "neutral":
          return this.colorService.neutral100
        case "red":
          return this.colorService.red100
        case "orange":
          return this.colorService.orange100
        case "yellow":
          return this.colorService.yellow100
        case "green":
          return this.colorService.green100
        case "teal":
          return this.colorService.teal100
        case "blue":
          return this.colorService.blue100
        case "brand":
          return this.colorService.brand100
        case "purple":
          return this.colorService.purple100
        case "pink":
          return this.colorService.pink100
      }
    }
  }
  public getNotificationTextColor(): string {
    switch (this.selectedColor) {
      case "neutral":
        return this.colorService.neutral800
      case "red":
        return this.colorService.red800
      case "orange":
        return this.colorService.orange800
      case "yellow":
        return this.colorService.yellow800
      case "green":
        return this.colorService.green800
      case "teal":
        return this.colorService.teal800
      case "blue":
        return this.colorService.blue800
      case "brand":
        return this.colorService.brand800
      case "purple":
        return this.colorService.purple800
      case "pink":
        return this.colorService.pink800
    }
  }

  public getNotificationBackgroundColor(): string {
    switch (this.selectedColor) {
      case "neutral":
        return this.colorService.neutral100
      case "red":
        return this.colorService.red100
      case "orange":
        return this.colorService.orange100
      case "yellow":
        return this.colorService.yellow100
      case "green":
        return this.colorService.green100
      case "teal":
        return this.colorService.teal100
      case "blue":
        return this.colorService.blue100
      case "brand":
        return this.colorService.brand100
      case "purple":
        return this.colorService.purple100
      case "pink":
        return this.colorService.pink100
    }
  }

  public getNotificationBorderColor(): string {
    switch (this.selectedColor) {
      case "neutral":
        return this.colorService.neutral200
      case "red":
        return this.colorService.red200
      case "orange":
        return this.colorService.orange200
      case "yellow":
        return this.colorService.yellow200
      case "green":
        return this.colorService.green200
      case "teal":
        return this.colorService.teal200
      case "blue":
        return this.colorService.blue200
      case "brand":
        return this.colorService.brand200
      case "purple":
        return this.colorService.purple200
      case "pink":
        return this.colorService.pink200
    }
  }
}

export type FlowTab = { title: string; notification?: number; image?: string; isDisabled?: boolean }
export type FlowColors = "neutral" | "red" | "orange" | "yellow" | "green" | "teal" | "blue" | "brand" | "purple" | "pink"
